import { Box } from '@mui/material';
import {
    GridToolbarColumnsButton, // keep these for now. Switching to a custom toolbar is more involved.
    GridToolbarContainer, GridToolbarDensitySelector, GridToolbarFilterButton,
} from '@mui/x-data-grid';

import { CsvExportButton, ExportButton, ChartButton } from './ToolbarComponents';
import { ToolBarIdProvider } from './ToolBarIdContext';

/**
 * Layout shell for a DataGrid toolbar slot - no screening knowledge. Column
 * visibility/density are baked in unconditionally (see the note below); export, filter,
 * and chart are props here rather than children, because - unlike a screening
 * EditCompoundsButton - none of them ever needs GridToolBar to know anything
 * screening-specific to render them, and no real caller has ever wanted them in a
 * different relative order from each other (confirmed against every current caller).
 * `children`, rendered first, is the one remaining extension point: for a leading
 * button that genuinely is caller-specific content GridToolBar has no business
 * constructing itself (e.g. screening's EditCompoundsButton) - that still arrives
 * fully-built from the caller, same as before, never as a flag GridToolBar interprets.
 * `toolBarId` is provided once via ToolBarIdContext.jsx here, not threaded to every
 * button individually - see that file's useToolBarId, and note the caller-built
 * leading child picks it up the same way despite being constructed outside this
 * function: Context scoping follows the rendered tree, not where the JSX was written.
 *
 * Column visibility and density are baked in unconditionally, not props or children:
 * every current caller wants both, always, with zero variation - that's not a caller
 * decision the way export/filter/chart/leading are, it's this toolbar's own baseline
 * chrome, so treating it as something each caller must assert was manufacturing a
 * degree of freedom nothing actually uses.
 * @param {{
 *   toolBarId: string, exportFileName?: string,
 *   extraExportOptions?: ({title: string, disabled?: boolean, handleExport: () => void} | null)[],
 *   charts?: string[], setChart?: (chart: string) => void, useFilter?: boolean,
 *   children?: React.ReactNode,
 * }} param0
 */
export function GridToolBar({
    children, toolBarId, exportFileName, extraExportOptions,
    charts, setChart, useFilter, ...containerProps
}) {
    return (
        <ToolBarIdProvider value={toolBarId}>
            <GridToolbarContainer sx={{ justifyContent: 'flex-start' }} {...containerProps}>
                {children}
                <GridToolbarColumnsButton />
                <GridToolbarDensitySelector />
                <CsvExportButton fileName={exportFileName} extraOptions={extraExportOptions} />
                {useFilter && <GridToolbarFilterButton />}
                <ChartButton charts={charts} setActiveChart={setChart} />
            </GridToolbarContainer>
        </ToolBarIdProvider>
    );
}

/**
 * Layout shell for the row above a Coral map - no screening knowledge. `exportOptions`,
 * `charts`, and `setChart` are props here rather than children, mirroring GridToolBar
 * above: neither ExportButton nor ChartButton needs any screening-specific knowledge to
 * render, and no real caller (ScoringCoralMap, ScreeningCoralMap) has ever wanted them
 * in a different relative order. `children`, rendered first, stays the extension point
 * for the one thing that genuinely is caller-specific content this shell has no
 * business constructing itself - AddCompoundButton or EditCompoundsButton, depending on
 * the caller - same as GridToolBar's leading child. `toolBarId` is provided via
 * ToolBarIdContext, same mechanism as GridToolBar.
 * @param {{
 *   toolBarId: string, exportOptions?: {}[], charts?: string[],
 *   setChart?: (chart: string) => void, sx?: object, children?: React.ReactNode,
 * }} param0
 */
export function CoralToolbar({
    children, toolBarId, exportOptions, charts, setChart, sx,
}) {
    return (
        <ToolBarIdProvider value={toolBarId}>
            <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '8px',
                padding: '4px 4px 0px',
                alignItems: 'center',
                border: '1px solid lightgray',
                ...sx,
            }}
            >
                {children}
                <ExportButton exportOptions={exportOptions} />
                <ChartButton charts={charts} setActiveChart={setChart} />
            </Box>
        </ToolBarIdProvider>
    );
}
